
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Heart, Target, Eye, Users, Lightbulb } from 'lucide-react';
import { useAccessibility } from '../hooks/useAccessibility';
import { ScrollReveal } from '../components/ScrollReveal';
import gpticamLogo from '../assets/gpticam.png';
import capesLogo from '../assets/capes.png';
import inovaLogo from '../assets/inovaedu.png';
import ifapLogo from '../assets/ifap.png';

const Equipe = () => {
  const { speak } = useAccessibility();

  const valores = [
    {
      icon: Heart,
      titulo: 'Inclusão',
      descricao: 'Acreditamos que toda criança tem direito à comunicação e desenvolvimento pleno.'
    },
    {
      icon: Lightbulb,
      titulo: 'Inovação',
      descricao: 'Utilizamos tecnologia de ponta para criar soluções acessíveis e eficazes.'
    },
    {
      icon: Users,
      titulo: 'Comunidade',
      descricao: 'Construímos uma rede de apoio entre famílias, educadores e profissionais.'
    },
    {
      icon: Target,
      titulo: 'Propósito',
      descricao: 'Cada funcionalidade é pensada para gerar impacto real na vida das crianças.'
    }
  ];

  const equipe = [
    {
      nome: 'Emellin Rayanne',
      cargo: 'Mãe Atípica',
      bio: 'Psicóloga especialista em TEA com 15 anos de experiência em tecnologia assistiva.',
      foto: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop&crop=face'
    },
    {
      nome: 'Klenilmar Dias',
      cargo: 'CTO',
      bio: 'Doutor em Inteligência Artificial (UFMG).',
      foto: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face'
    },
    {
      nome: 'Abymael Rennare',
      cargo: '',
      bio: 'Designer focada em experiências inclusivas e interfaces acessíveis.',
      foto: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=300&fit=crop&crop=face'
    },
    {
      nome: 'Dr. João Oliveira',
      cargo: 'Consultor Médico',
      bio: 'Neurologista pediátrico especialista em Transtornos do Neurodesenvolvimento.',
      foto: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=300&h=300&fit=crop&crop=face'
    }
  ];

  const parceiros = [
    {
      nome: 'GPTICAM',
      tipo: 'Grupo de pesquisa responsável',
      logo: gpticamLogo,
      descricao: 'Grupo de Pesquisa em Tecnologias da Informação e Comunicação na Amazônia — desenvolvimento e validação científica do CONECTAR.',
    },
    {
      nome: 'CAPES',
      tipo: 'Fomento à pesquisa',
      logo: capesLogo,
      descricao: 'Coordenação de Aperfeiçoamento de Pessoal de Nível Superior, do Ministério da Educação.',
    },
    {
      nome: 'Inova Educação',
      tipo: '2º lugar · Edital CAPES 2025',
      logo: inovaLogo,
      descricao: 'Edital Nacional CAPES/InovaEducação 2025 — validação de maturidade técnica (TRL 6) após avaliação de banca especialista.',
    },
    {
      nome: 'IFAP – Campus Macapá',
      tipo: 'Instituição de ensino',
      logo: ifapLogo,
      descricao: 'Instituto Federal do Amapá, Campus Macapá — apoio institucional ao desenvolvimento do projeto.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.35] dark:opacity-[0.12]"
          style={{ backgroundImage: 'radial-gradient(rgb(var(--primary)) 1px, transparent 1px)', backgroundSize: '26px 26px' }}
          aria-hidden="true"
        />
        <div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--primary)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-16 -right-16 w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--warm)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 dark:bg-primary/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-6">
              <Users className="w-4 h-4 text-primary-ink dark:text-primary-tint" />
              Nossa Equipe
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-[1.1]">
              As pessoas por trás do <span className="text-primary">CONECTAR</span>
            </h1>

            <p className="font-body text-lg md:text-xl text-muted-foreground leading-relaxed">
              Um time que nasceu de uma história real e cresceu com pesquisa, tecnologia
              e o compromisso de acolher crianças autistas e suas famílias.
            </p>
          </div>
        </div>
      </section>

      {/* Equipe */}
      <ScrollReveal>
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {equipe.map((membro, index) => (
              <div
                key={index}
                className={`rounded-[1.75rem] p-6 text-center ${
                  index % 2 === 0 ? 'bg-primary/[0.05] dark:bg-primary/[0.1]' : 'bg-warm/[0.06] dark:bg-warm/[0.11]'
                }`}
              >
                <img
                  src={membro.foto}
                  alt={membro.nome}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="font-semibold text-foreground text-lg mb-1">{membro.nome}</h3>
                <p className="text-primary text-sm font-medium mb-3">{membro.cargo}</p>
                <p className="text-muted-foreground text-sm leading-relaxed">{membro.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Missão, Visão, Valores */}
      <ScrollReveal>
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">Missão</h2>
              <p className="text-muted-foreground leading-relaxed">
                Desenvolver tecnologias inclusivas que potencializem a comunicação, 
                aprendizado e desenvolvimento de crianças autistas, promovendo sua 
                autonomia e qualidade de vida.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">Visão</h2>
              <p className="text-muted-foreground leading-relaxed">
                Ser referência mundial em tecnologia assistiva para autismo, 
                criando um futuro onde toda criança autista tenha acesso a 
                ferramentas que a empoderem.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">Valores</h2>
              <p className="text-muted-foreground leading-relaxed">
                Inclusão, empatia, inovação responsável, respeito à 
                neurodiversidade e compromisso com o impacto social positivo 
                em todas as nossas ações.
              </p>
            </div>
          </div>

          {/* Valores Detalhados */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valores.map((valor, index) => (
              <div
                key={index}
                className={`rounded-[1.75rem] p-6 text-center ${
                  index % 2 === 0 ? 'bg-primary/[0.05] dark:bg-primary/[0.1]' : 'bg-warm/[0.06] dark:bg-warm/[0.11]'
                }`}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <valor.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{valor.titulo}</h3>
                <p className="text-sm text-muted-foreground">{valor.descricao}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* História do Projeto */}
      <ScrollReveal>
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              Nossa História
            </h2>
            
            <div className="rounded-[2rem] bg-primary/[0.05] dark:bg-primary/[0.1] p-8">
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed mb-6">
                  O projeto CONECTAR nasceu em 2022, quando nossa mãe atípica, Emellin Rayanne, 
                  percebeu a necessidade de ferramentas tecnológicas mais acessíveis e 
                  personalizadas para seu filho Emanuel Reymond durante o uso delas para o tratamento de reforço visual e sonoro 
                  em jogos e aplicativos que utlizavam cartões para identificar pessoas, letras, números, objetos e demais categorias.
                </p>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Vendo essa dificuldade para encontrar recursos adequados e a
                  falta de opções verdadeiramente inclusivas no mercado, ela 
                  escreveu um projeto de pesquisa que tinha o objetivo de criar um aplicativo 
                  que permitia criar categorias personalizáveis. Assim nasceu o CONECTAR. 
                </p>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Hoje, o CONECTAR é mais que um aplicativo - é uma plataforma que conecta 
                  famílias, educadores e profissionais em uma missão comum: criar um mundo 
                  mais inclusivo e acessível para crianças autistas.
                </p>
                
              {/*   <div className="grid md:grid-cols-3 gap-4 mt-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary mb-1">50k+</div>
                    <div className="text-sm text-muted-foreground">Famílias impactadas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary mb-1">200+</div>
                    <div className="text-sm text-muted-foreground">Escolas parceiras</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary mb-1">15+</div>
                    <div className="text-sm text-muted-foreground">Países atendidos</div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
      </section>
      </ScrollReveal>

      {/* Parceiros */}
      <ScrollReveal>
      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Parceiros e Apoios
            </h2>
            <p className="text-lg text-muted-foreground">
              Organizações que confiam e apoiam nossa missão
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {parceiros.map((parceiro, index) => (
              <div key={index} className="rounded-xl bg-background shadow-md p-6 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="h-14 flex items-center justify-center mb-3">
                  <img src={parceiro.logo} alt={parceiro.nome} className="max-h-14 max-w-[120px] object-contain" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">{parceiro.nome}</h3>
                <p className="text-sm text-primary mb-2">{parceiro.tipo}</p>
                <p className="font-body text-xs text-muted-foreground leading-relaxed">{parceiro.descricao}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal>
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Faça parte da nossa jornada
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Junte-se a nós na missão de criar um mundo mais inclusivo. 
            Baixe o CONECTAR e veja a diferença que a tecnologia pode fazer.
          </p>
          
          <button 
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 rounded-lg font-medium text-lg transition-colors inline-flex items-center space-x-2"
            onClick={() => speak('Baixar aplicativo CONECTAR')}
          >
            <span>Baixar App Grátis</span>
          </button>
        </div>
      </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
};

export default Equipe;
